import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MdArrowForward } from 'react-icons/md';
import { useAuthStore } from '../store/authStore';
import { useAuthModalStore } from '../store/authModalStore';
import '../styles/AboutPage.css';

/**
 * About Us — marketing page for VIN Wholesale. Copy is built from the
 * platform's established positioning (B2B wholesale distribution; efficiency,
 * reliability, scale) and reuses the same contact details surfaced elsewhere
 * in the app. Shares the brand palette via AboutPage.css.
 */
const VALUES = [
  {
    icon: '⚡',
    title: 'Efficiency',
    text: 'A single platform for ordering, fulfilment, and tracking — so your shelves stay stocked without the back-and-forth.',
  },
  {
    icon: '🛡️',
    title: 'Reliability',
    text: 'Consistent delivery windows and real-time order visibility your business can plan around, day in and day out.',
  },
  {
    icon: '📈',
    title: 'Scale',
    text: 'Built to grow with you — from a single store to a multi-location operation, on the same dependable supply chain.',
  },
];

const STATS = [
  { value: '500+', label: 'Retail Partners' },
  { value: 'Mon–Sat', label: '7 AM – 8 PM Support' },
  { value: '1 Day', label: 'Avg. Response Time' },
  { value: 'Real-time', label: 'Order Tracking' },
];

const AboutPage: React.FC = () => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const openRegister = useAuthModalStore((s) => s.openRegister);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="about-page">
      {/* Hero */}
      <section className="about-hero">
        <span className="about-hero__tag">ABOUT US</span>
        <h1 className="about-hero__title">
          Wholesale distribution,<br />built for retailers.
        </h1>
        <p className="about-hero__subtitle">
          VIN Wholesale is a business-to-business distributor on a mission to make
          restocking effortless. We pair a dependable supply chain with a modern
          ordering platform, so retail partners can spend less time managing
          inventory and more time running their business.
        </p>
      </section>

      {/* Story */}
      <section className="about-section">
        <h2 className="about-section__heading">Who we are</h2>
        <p>
          We supply the products retailers rely on every day, and we back every
          order with verification, fair pricing, and accountable delivery. As a
          wholesale partner, our job is simple: get the right products to the
          right stores, on time, every time.
        </p>
        <p>
          Behind the scenes, our platform connects ordering, inventory, and live
          delivery tracking into one place — the same system our team uses to keep
          hundreds of retail partners stocked and selling.
        </p>
      </section>

      {/* Values */}
      <section className="about-section">
        <h2 className="about-section__heading">What we stand for</h2>
        <div className="about-values">
          {VALUES.map((v) => (
            <div key={v.title} className="about-value-card">
              <span className="about-value-card__icon">{v.icon}</span>
              <h3 className="about-value-card__title">{v.title}</h3>
              <p className="about-value-card__text">{v.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Stats */}
      <section className="about-stats">
        {STATS.map((s) => (
          <div key={s.label} className="about-stat">
            <span className="about-stat__value">{s.value}</span>
            <span className="about-stat__label">{s.label}</span>
          </div>
        ))}
      </section>

      {/* CTA */}
      <section className="about-cta">
        <h2 className="about-cta__heading">Ready to stock smarter?</h2>
        <p className="about-cta__text">
          Join the retailers who trust VIN Wholesale to keep their shelves full.
        </p>
        <div className="about-cta__actions">
          {!isAuthenticated && (
            <button
              type="button"
              onClick={openRegister}
              className="about-cta__btn about-cta__btn--primary"
            >
              Become a Partner <MdArrowForward />
            </button>
          )}
          <Link to="/contact" className="about-cta__btn about-cta__btn--ghost">
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
